/* ==========================================================================
   AUGUST ONLINE — PDP behaviour
   Deliberately defines NO element that Dawn already owns. Add to cart runs
   through Dawn's <product-form>, quantity through this file's <au-quantity>
   only if Dawn's <quantity-input> is unavailable, and variant changes use
   Dawn's Section Rendering API so price and availability come back as Liquid.
   ========================================================================== */
(function () {
  'use strict';

  /* More than one section loads this file, and duplicate <script src> tags
     each execute. Custom element definitions are already guarded, but the
     delegated listeners and observers are not, so bail on a second run. */
  if (window.__auPdpLoaded) return;
  window.__auPdpLoaded = true;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function scrollBehavior() {
    return REDUCED.matches ? 'auto' : 'smooth';
  }

  function define(name, ctor) {
    if (!window.customElements || customElements.get(name)) return;
    customElements.define(name, ctor);
  }

  /* ------------------------------------------------------------------------
     Gallery — a scroll-snap rail, so mobile swipe comes for free and the
     desktop thumbs just drive scrollLeft.
     ---------------------------------------------------------------------- */
  var AuGallery = class extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('[data-au-frame]');
      if (!this.frame) return;

      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-au-slide]'));
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-au-thumb]'));

      this.thumbs.forEach(
        function (thumb) {
          thumb.addEventListener(
            'click',
            function () {
              this.select(thumb.getAttribute('data-media-id'));
            }.bind(this)
          );
        }.bind(this)
      );

      Array.prototype.forEach.call(
        this.querySelectorAll('[data-au-step]'),
        function (button) {
          button.addEventListener(
            'click',
            function () {
              this.step(parseInt(button.getAttribute('data-au-step'), 10));
            }.bind(this)
          );
        }.bind(this)
      );

      this.activeIndex = -1;
      this.watchSlides();

      /* Liquid marks the thumb for the selected variant's image, which isn't
         always the first one. Start there instead of assuming index 0. */
      var initial = 0;
      for (var i = 0; i < this.thumbs.length; i++) {
        if (this.thumbs[i].classList.contains('is-active')) {
          initial = i;
          break;
        }
      }

      if (initial > 0) {
        this.goTo(initial, 'auto');
      } else {
        this.setActive(0);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.frame) this.frame.removeEventListener('scroll', this.onScroll);
    }

    /* Measure rather than infer. IntersectionObserver only reports elements
       whose intersection *changed*, so the callback often can't see the slide
       that's actually centred, and comparing ratios between a tall slide and
       a partly-visible one picks the wrong winner. Nearest-centre is exact. */
    watchSlides() {
      var ticking = false;

      this.onScroll = function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(
          function () {
            ticking = false;
            this.syncActive();
          }.bind(this)
        );
      }.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.frame.addEventListener('scroll', this.onScroll, { passive: true });
    }

    /* Stacked: the slide nearest the middle of the viewport.
       Rail: the slide nearest the middle of the frame. */
    syncActive() {
      var index = this.currentIndex();
      if (index > -1) this.setActive(index);
    }

    currentIndex() {
      if (!this.slides.length) return -1;

      var stacked = this.isStacked;
      var middle;

      if (stacked) {
        middle = window.innerHeight / 2;
      } else {
        var frameBox = this.frame.getBoundingClientRect();
        middle = frameBox.left + frameBox.width / 2;
      }

      var best = 0;
      var bestDelta = Infinity;

      this.slides.forEach(function (slide, i) {
        var box = slide.getBoundingClientRect();
        var centre = stacked ? box.top + box.height / 2 : box.left + box.width / 2;
        var delta = Math.abs(centre - middle);

        if (delta < bestDelta) {
          bestDelta = delta;
          best = i;
        }
      });

      return best;
    }

    indexOfMedia(mediaId) {
      for (var i = 0; i < this.slides.length; i++) {
        if (this.slides[i].getAttribute('data-media-id') === String(mediaId)) return i;
      }
      return -1;
    }

    select(mediaId) {
      var index = this.indexOfMedia(mediaId);
      if (index < 0) return;
      this.goTo(index);
    }

    get index() {
      return this.activeIndex > -1 ? this.activeIndex : this.currentIndex();
    }

    step(delta) {
      var next = this.index + delta;
      if (next < 0) next = this.slides.length - 1;
      if (next > this.slides.length - 1) next = 0;
      this.goTo(next);
    }

    /* Desktop stacks the images and lets the page scroll through them;
       narrower screens keep the horizontal rail inside the frame. */
    get isStacked() {
      return window.matchMedia('(min-width: 990px)').matches;
    }

    goTo(index, behavior) {
      var slide = this.slides[index];
      if (!slide) return;

      /* Mark it straight away so a thumb click feels instant, rather than
         waiting for the scroll to settle. */
      this.setActive(index);

      var how = behavior || scrollBehavior();

      if (this.isStacked) {
        var chrome = parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--au-chrome-h'),
          10
        );
        if (isNaN(chrome)) chrome = 88;

        window.scrollTo({
          top: slide.getBoundingClientRect().top + window.pageYOffset - chrome - 24,
          behavior: how
        });
        return;
      }

      this.frame.scrollTo({ left: slide.offsetLeft, behavior: how });
    }

    /* scrollIntoView was the glitch: it scrolls every scrollable ancestor,
       so it jerked the thumb rail and sometimes nudged the page as well. This
       scrolls the rail only, and only when the thumb is actually out of view. */
    revealThumb(thumb) {
      var list = thumb.parentElement && thumb.parentElement.closest('.au-pdp__thumbs');
      if (!list) return;

      var listBox = list.getBoundingClientRect();
      var thumbBox = thumb.getBoundingClientRect();
      var vertical = list.scrollHeight > list.clientHeight + 1;
      var horizontal = list.scrollWidth > list.clientWidth + 1;
      var smooth = REDUCED.matches ? 'auto' : 'smooth';

      if (vertical) {
        if (thumbBox.top >= listBox.top && thumbBox.bottom <= listBox.bottom) return;
        list.scrollBy({
          top: thumbBox.top - listBox.top - (listBox.height - thumbBox.height) / 2,
          behavior: smooth
        });
        return;
      }

      if (!horizontal) return;
      if (thumbBox.left >= listBox.left && thumbBox.right <= listBox.right) return;

      list.scrollBy({
        left: thumbBox.left - listBox.left - (listBox.width - thumbBox.width) / 2,
        behavior: smooth
      });
    }

    setActive(index) {
      var slide = this.slides[index];
      if (!slide || index === this.activeIndex) return;

      this.activeIndex = index;
      var mediaId = slide.getAttribute('data-media-id');

      var stage = this.querySelector('.au-pdp__stage');
      if (stage) stage.style.setProperty('--au-progress-index', index);

      this.thumbs.forEach(
        function (thumb) {
          var on = thumb.getAttribute('data-media-id') === mediaId;
          thumb.classList.toggle('is-active', on);
          thumb.setAttribute('aria-current', on ? 'true' : 'false');
          if (on) this.revealThumb(thumb);
        }.bind(this)
      );

      this.dispatchEvent(
        new CustomEvent('au:gallery:change', {
          bubbles: true,
          detail: { index: index, mediaId: mediaId }
        })
      );

      // Pause any video that scrolled out of view.
      this.slides.forEach(function (item, i) {
        if (i === index) return;
        var video = item.querySelector('video');
        if (video && !video.paused) video.pause();
      });
    }
  };

  define('au-gallery', AuGallery);

  /* ------------------------------------------------------------------------
     Variant picker — mirrors Dawn's VariantSelects logic, but under its own
     tag name so it can never collide with the theme's own definition.
     ---------------------------------------------------------------------- */
  var AuVariantPicker = class extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.formId = this.dataset.form;
      this.variants = this.readVariants();
      this.abortController = null;

      this.addEventListener('change', this.onChange.bind(this));
      this.updateAvailability();
    }

    readVariants() {
      var node = this.querySelector('[data-au-variants]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return [];
      }
    }

    get inputs() {
      return Array.prototype.slice.call(this.querySelectorAll('.au-pdp__opt-input'));
    }

    get selectedOptions() {
      return this.inputs
        .filter(function (input) {
          return input.checked;
        })
        .map(function (input) {
          return input.value;
        });
    }

    onChange() {
      var selected = this.selectedOptions;

      this.currentVariant =
        this.variants.filter(function (variant) {
          return variant.options.every(function (option, index) {
            return option === selected[index];
          });
        })[0] || null;

      this.updateSelectedLabels(selected);
      this.updateAvailability();
      this.updateFormInput();
      this.updateMedia();

      if (!this.currentVariant) {
        this.setUnavailable();
        this.publish(null);
        return;
      }

      this.updateURL();
      this.renderProductInfo();
      this.publish(this.currentVariant);
    }

    publish(variant) {
      document.dispatchEvent(
        new CustomEvent('au:variant:change', {
          detail: { variant: variant, sectionId: this.sectionId, productUrl: this.productUrl }
        })
      );
    }

    updateSelectedLabels(selected) {
      Array.prototype.forEach.call(
        this.querySelectorAll('[data-au-selected-for]'),
        function (node) {
          var position = parseInt(node.getAttribute('data-au-selected-for'), 10);
          if (selected[position] !== undefined) node.textContent = selected[position];
        }
      );
    }

    /* A value is offered if some in-stock variant matches every option chosen
       before it — the same progressive rule Dawn uses. */
    updateAvailability() {
      var selected = this.selectedOptions;
      var variants = this.variants;

      this.inputs.forEach(function (input) {
        var position = parseInt(input.getAttribute('data-option-position'), 10);
        var available = variants.some(function (variant) {
          if (!variant.available) return false;
          if (variant.options[position] !== input.value) return false;
          for (var i = 0; i < position; i++) {
            if (selected[i] !== undefined && variant.options[i] !== selected[i]) return false;
          }
          return true;
        });

        var label = input.nextElementSibling;
        if (label) label.classList.toggle('is-unavailable', !available);
        input.setAttribute('data-available', available ? 'true' : 'false');
      });
    }

    updateFormInput() {
      var form = document.getElementById(this.formId);
      if (!form) return;
      var input = form.querySelector('input[name="id"]');
      if (!input) return;

      if (this.currentVariant) {
        input.value = this.currentVariant.id;
        input.disabled = false;
      } else {
        input.disabled = true;
      }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateMedia() {
      if (!this.currentVariant || !this.currentVariant.featured_media) return;
      var gallery = document.getElementById('AuGallery-' + this.sectionId);
      if (gallery && typeof gallery.select === 'function') {
        gallery.select(this.currentVariant.featured_media.id);
      }
    }

    updateURL() {
      if (!this.currentVariant) return;
      window.history.replaceState(
        {},
        '',
        this.productUrl + '?variant=' + this.currentVariant.id
      );
    }

    setUnavailable() {
      var price = document.getElementById('au-price-' + this.sectionId);
      if (price) price.classList.add('is-hidden');

      var text = (window.variantStrings && window.variantStrings.unavailable) || 'Unavailable';

      [
        ['au-atc', '.au-pdp__atc-label'],
        ['au-sticky-atc', '.au-stickybar__btn-label']
      ].forEach(
        function (pair) {
          var button = document.getElementById(pair[0] + '-' + this.sectionId);
          if (!button) return;
          button.setAttribute('disabled', 'disabled');
          var label = button.querySelector(pair[1]);
          if (label) label.textContent = text;
        }.bind(this)
      );
    }

    /* Dawn's Section Rendering API: price, stock line and the button come
       back as rendered Liquid, so money formatting and translations stay
       wherever the theme already put them. */
    renderProductInfo() {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      var url =
        this.productUrl +
        (this.productUrl.indexOf('?') > -1 ? '&' : '?') +
        'variant=' +
        this.currentVariant.id +
        '&section_id=' +
        this.sectionId;

      fetch(url, { signal: this.abortController.signal })
        .then(function (response) {
          return response.text();
        })
        .then(
          function (text) {
            var parsed = new DOMParser().parseFromString(text, 'text/html');
            var ids = ['au-price', 'au-stock', 'au-sku', 'au-sticky-price'];

            ids.forEach(
              function (id) {
                var source = parsed.getElementById(id + '-' + this.sectionId);
                var target = document.getElementById(id + '-' + this.sectionId);
                if (source && target) {
                  target.innerHTML = source.innerHTML;
                  target.className = source.className;
                }
              }.bind(this)
            );

            ['au-atc', 'au-sticky-atc'].forEach(
              function (id) {
                var sourceButton = parsed.getElementById(id + '-' + this.sectionId);
                var targetButton = document.getElementById(id + '-' + this.sectionId);
                if (!sourceButton || !targetButton) return;

                targetButton.innerHTML = sourceButton.innerHTML;
                if (sourceButton.hasAttribute('disabled')) {
                  targetButton.setAttribute('disabled', 'disabled');
                } else {
                  targetButton.removeAttribute('disabled');
                }
              }.bind(this)
            );
          }.bind(this)
        )
        .catch(function (error) {
          if (error.name !== 'AbortError') console.error(error);
        });
    }
  };

  define('au-variant-picker', AuVariantPicker);

  /* ------------------------------------------------------------------------
     Quantity — Dawn's <quantity-input> in global.js owns this, including any
     quantity rules. This only steps in if the theme doesn't define it, so the
     buttons are never dead.
     ---------------------------------------------------------------------- */
  function quantityFallback() {
    if (window.customElements && customElements.get('quantity-input')) return;

    Array.prototype.forEach.call(document.querySelectorAll('quantity-input'), function (wrapper) {
      if (wrapper.dataset.auBound) return;
      wrapper.dataset.auBound = 'true';

      var input = wrapper.querySelector('input');
      if (!input) return;

      wrapper.addEventListener('click', function (event) {
        var button = event.target.closest('button[name="plus"], button[name="minus"]');
        if (!button) return;
        event.preventDefault();

        var min = parseInt(input.min, 10) || 1;
        var step = button.name === 'plus' ? 1 : -1;
        var next = (parseInt(input.value, 10) || min) + step;

        input.value = Math.max(min, next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ------------------------------------------------------------------------
     Recommendations — same fetch-and-swap contract as Dawn's, loaded lazily.
     ---------------------------------------------------------------------- */
  var AuRecommendations = class extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;

      var load = this.load.bind(this);
      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '0px 0px 500px 0px' }
      );
      observer.observe(this);
    }

    load() {
      fetch(this.dataset.url)
        .then(function (response) {
          return response.text();
        })
        .then(
          function (text) {
            var parsed = new DOMParser().parseFromString(text, 'text/html');
            var fresh = parsed.querySelector('au-recommendations');

            if (!fresh || !fresh.innerHTML.trim().length) {
              var section = this.closest('.au-section');
              if (section) section.hidden = true;
              return;
            }

            this.innerHTML = fresh.innerHTML;
            this.classList.add('is-loaded');

            /* These cards arrive after every observer has already run, so
               they'd sit at opacity 0 forever otherwise. */
            revealFallback();

            /* Lets august-home.js re-bind wishlist / compare / quick view on
               cards that arrived after its own DOMContentLoaded pass. */
            document.dispatchEvent(
              new CustomEvent('au:products:added', { detail: { container: this } })
            );
          }.bind(this)
        )
        .catch(function (error) {
          console.error(error);
        });
    }
  };

  define('au-recommendations', AuRecommendations);

  /* ------------------------------------------------------------------------
     Scroll reveal — august-pdp.css now carries the [data-au-reveal] rule
     (opacity: 0 until .au-visible lands), so this file has to own the
     observer that clears it. Without one, the mosaic and contact sections
     hold their layout space while staying invisible. Adding the class twice
     is harmless if august-home.js is also observing.
     ---------------------------------------------------------------------- */
  function revealFallback() {
    var nodes = Array.prototype.slice.call(
      document.querySelectorAll(
        '.au-pdp [data-au-reveal], .au-mosaic [data-au-reveal],' +
          ' .au-recs [data-au-reveal], .au-contact [data-au-reveal],' +
          ' .au-mediarow [data-au-reveal]'
      )
    ).filter(function (node) {
      return !node.classList.contains('au-visible');
    });

    if (!nodes.length) return;

    function show(node) {
      node.classList.add('au-visible');
    }

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(show);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          show(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px' }
    );

    nodes.forEach(function (node) {
      observer.observe(node);
    });
  }

  /* ------------------------------------------------------------------------
     Loading state for buttons that submit through someone else's handler:
     the card quick-add form, the card size chips, and the sticky bar.

     august-shop.js owns those cart requests and doesn't report back, so the
     completion signal here is the toast it raises, with a timeout as a
     backstop. If you expose a resolved promise or dispatch an event, swap
     `watchForFinish` for that and delete the timeout.
     ---------------------------------------------------------------------- */
  var LOADING_TIMEOUT = 8000;
  var loadingTimer = null;

  function clearLoading() {
    clearTimeout(loadingTimer);
    Array.prototype.forEach.call(document.querySelectorAll('.is-loading'), function (node) {
      node.classList.remove('is-loading');
      node.removeAttribute('aria-busy');
    });
  }

  function startLoading(node) {
    if (!node || node.classList.contains('is-loading')) return;
    node.classList.add('is-loading');
    node.setAttribute('aria-busy', 'true');

    clearTimeout(loadingTimer);
    loadingTimer = setTimeout(clearLoading, LOADING_TIMEOUT);
  }

  function watchForFinish() {
    var toast = document.querySelector('.au-toast');
    if (toast) {
      new MutationObserver(clearLoading).observe(toast, {
        attributes: true,
        attributeFilter: ['class']
      });
      return;
    }

    /* The toast is injected on first use, so wait for it to appear once. */
    var bodyObserver = new MutationObserver(function () {
      if (!document.querySelector('.au-toast')) return;
      bodyObserver.disconnect();
      watchForFinish();
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  function bindLoadingStates() {
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || !form.matches) return;

        if (form.matches('form[data-au-add]')) {
          startLoading(form.querySelector('button[type="submit"]'));
          return;
        }

        /* Dawn's product form drives both the inline and the sticky button. */
        if (form.matches('.au-pdp__form')) {
          var sticky = document.querySelector('.au-stickybar__btn');
          if (sticky) startLoading(sticky);
        }
      },
      true
    );

    document.addEventListener(
      'click',
      function (event) {
        var chip = event.target.closest && event.target.closest('.au-size-chip[data-au-size-add]');
        if (chip) startLoading(chip);
      },
      true
    );

    /* Dawn publishes this once the cart responds. */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, clearLoading);
    }

    watchForFinish();
  }

  /* ------------------------------------------------------------------------
     Sticky add to cart — shown only once the real buy row leaves the screen,
     so the two are never on screen competing with each other.
     ---------------------------------------------------------------------- */
  function syncStickyQuantity(bar) {
    var mirror = bar.querySelector('[data-au-sticky-input]');
    var form = document.querySelector('.au-pdp__form');
    var real = form && form.querySelector('input[name="quantity"]');
    if (!mirror) return;

    /* No form to write into means the mirror would lie about what gets
       added, so hide it rather than show a control that does nothing. */
    if (!real) {
      var wrapper = bar.querySelector('[data-au-sticky-qty]');
      if (wrapper) wrapper.hidden = true;
      return;
    }

    function push() {
      var min = parseInt(mirror.min, 10) || 1;
      var value = Math.max(min, parseInt(mirror.value, 10) || min);
      mirror.value = value;
      real.value = value;
      real.dispatchEvent(new Event('change', { bubbles: true }));
    }

    bar.addEventListener('click', function (event) {
      var button = event.target.closest('[data-au-sticky-step]');
      if (!button) return;
      event.preventDefault();
      mirror.value =
        (parseInt(mirror.value, 10) || 1) + parseInt(button.dataset.auStickyStep, 10);
      push();
    });

    mirror.addEventListener('change', push);

    /* Keep the mirror honest if the shopper uses the main control instead. */
    real.addEventListener('change', function () {
      if (real.value !== mirror.value) mirror.value = real.value;
    });
  }

  function stickyBar() {
    var bar = document.querySelector('[data-au-stickybar]');
    var anchor = document.querySelector('.au-pdp__buy-row');
    if (!bar || !anchor) return;

    bar.hidden = false;
    syncStickyQuantity(bar);

    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          /* Only pin it when the buy row has scrolled off the top — not when
             it's still below the fold on first paint. */
          var passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-on', passed);
        });
      },
      { threshold: 0 }
    );

    observer.observe(anchor);
  }

  /* ------------------------------------------------------------------------
     Sticky offset fallback — august-home.js normally publishes --au-chrome-h.
     ---------------------------------------------------------------------- */
  function measureChrome() {
    var root = document.documentElement;
    if (getComputedStyle(root).getPropertyValue('--au-chrome-h').trim()) return;

    var header = document.querySelector('.au-headbar, .shopify-section-group-header-group');
    if (!header) return;
    root.style.setProperty('--au-chrome-h', Math.round(header.offsetHeight) + 'px');
  }

  function onReady() {
    measureChrome();
    quantityFallback();
    revealFallback();
    bindLoadingStates();
    stickyBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
  window.addEventListener('resize', measureChrome);
})();